import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { entregarFichero, generarRelleno } from "@/lib/documentos/subir";
import type { Relleno } from "@/lib/documentos/rellenar";

/** Lo que manda la edge (`document_fill`): qué documento y qué va dónde. */
export interface DocumentFillItem {
  v: 1;
  document_id: string;
  file_name: string;
  kind: string;
  sheet: string;
  rows: number;
  relleno: Relleno;
}

export function leerItemRelleno(raw: Record<string, unknown>): DocumentFillItem | null {
  if (!raw || raw.v !== 1 || typeof raw.document_id !== "string" || typeof raw.file_name !== "string") return null;
  const r = raw.relleno as Relleno | undefined;
  if (!r || !Array.isArray(r.tablas) || !Array.isArray(r.campos)) return null;
  return {
    v: 1,
    document_id: raw.document_id,
    file_name: raw.file_name,
    kind: String(raw.kind ?? ""),
    sheet: String(raw.sheet ?? ""),
    rows: Number(raw.rows) || 0,
    relleno: r,
  };
}

/**
 * «Tu documento, relleno» (28-09-2026): el fichero de la PERSONA con los datos que
 * decidió el asistente, en su mismo formato. Se genera al pulsar (se baja el
 * original y se escribe en él: src/lib/documentos/rellenar.ts); nada se guarda.
 */
export function AssistantDocumentFillCard({ item }: { item: Record<string, unknown> }) {
  const { t } = useTranslation();
  const [ocupado, setOcupado] = useState(false);
  const it = leerItemRelleno(item);
  if (!it) return null;
  const Icono = it.kind === "docx" ? FileText : FileSpreadsheet;
  const color = it.kind === "docx" ? "#2B579A" : "#1D7A46";

  const descargar = async () => {
    if (ocupado) return;
    setOcupado(true);
    try {
      const f = await generarRelleno(supabase, it.document_id, it.relleno);
      await entregarFichero(f.blob, f.nombre, f.mime);
    } catch (e) {
      console.error("[AssistantDocumentFillCard]", e);
      toast.error(t("ipmAssistant.documents.fillError", "No se pudo preparar el documento. Inténtalo otra vez."));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div data-testid="assistant-document-fill-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: color, boxShadow: `0 6px 14px ${color}42` }}>
          <Icono className="h-5 w-5 text-white" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("ipmAssistant.documents.fillTitle", "Tu documento, relleno")}
          </span>
          <span className="block truncate text-sm font-bold text-foreground">{it.file_name}</span>
          <span data-testid="assistant-document-fill-meta" className="block truncate text-[11px] text-muted-foreground">
            {t("ipmAssistant.documents.fillMeta", "{{rows}} filas en «{{sheet}}»", { rows: it.rows, sheet: it.sheet })}
          </span>
        </span>
      </div>
      <button
        type="button"
        onClick={descargar}
        disabled={ocupado}
        aria-busy={ocupado}
        data-testid="assistant-document-fill-download"
        className="mt-2.5 inline-flex min-h-[38px] w-full items-center justify-center gap-1.5 rounded-xl border px-3 text-[13px] font-semibold transition-all active:scale-[0.98] disabled:opacity-60"
        style={{ borderColor: `${color}55`, backgroundColor: `${color}0F`, color }}
      >
        {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {t("ipmAssistant.documents.fillDownload", "Descargar")}
      </button>
    </div>
  );
}
